/**
 * API Error Handler - bridges raw API call paths (fetch + axios) into the
 * shared ErrorToast with a visible Error ID.
 *
 * Both helpers normalise an arbitrary API failure into the shared
 * `ErrorResponse` contract and then delegate to `ErrorHandler.displayError`,
 * which renders the UI and throws `ErrorDisplayed`.
 */

import { ErrorResponse, ErrorIdGenerator } from '@akfdev-com/shared';
import { ErrorHandler } from './errorHandler';

/**
 * Type guard: does an arbitrary parsed body match the shared ErrorResponse
 * contract closely enough to forward as-is? We require the two fields the UI
 * depends on: a non-empty errorId and a userMessage.
 */
function isErrorResponseShape(body: unknown): body is ErrorResponse {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const candidate = body as Record<string, unknown>;
  return (
    typeof candidate.errorId === 'string' &&
    candidate.errorId.length > 0 &&
    typeof candidate.userMessage === 'string' &&
    candidate.userMessage.length > 0
  );
}

/**
 * Generate a deterministic fallback Error ID so backend/frontend can correlate
 * even when the server response did not include one.
 */
function fallbackErrorId(statusCode: number, message: string): string {
  return ErrorIdGenerator.generate({
    errorType: 'ApiError',
    message: `${statusCode}:${message}`,
    context: { statusCode },
  });
}

/**
 * Pull the best available user-facing message from an arbitrary response body.
 */
function extractUserMessage(body: unknown, statusCode: number): string {
  if (body && typeof body === 'object') {
    const candidate = body as Record<string, unknown>;
    if (typeof candidate.userMessage === 'string' && candidate.userMessage) {
      return candidate.userMessage;
    }
    if (typeof candidate.error === 'string' && candidate.error) {
      return candidate.error;
    }
    if (typeof candidate.message === 'string' && candidate.message) {
      return candidate.message;
    }
  }
  return statusCode >= 500
    ? 'Something went wrong. Please try again or contact support.'
    : 'The request could not be completed. Please try again or contact support.';
}

/**
 * Build a normalised ErrorResponse from a status code + arbitrary body.
 */
function buildErrorResponse(statusCode: number, body: unknown): ErrorResponse {
  if (isErrorResponseShape(body)) {
    return body;
  }

  const userMessage = extractUserMessage(body, statusCode);
  const bodyErrorId =
    body && typeof body === 'object'
      ? (body as Record<string, unknown>).errorId
      : undefined;
  const errorId =
    typeof bodyErrorId === 'string' && bodyErrorId.length > 0
      ? bodyErrorId
      : fallbackErrorId(statusCode, userMessage);

  return {
    statusCode,
    errorId,
    userMessage,
    timestamp: new Date().toISOString(),
    retryable: statusCode >= 500,
  };
}

/**
 * Read the body of a failed fetch Response, normalise it to an ErrorResponse,
 * and route it through ErrorHandler.displayError (which throws ErrorDisplayed).
 *
 * Always throws — the return type is `never`.
 */
export async function displayApiErrorFromResponse(
  response: Response,
): Promise<never> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  const errorResponse = buildErrorResponse(response.status, body);
  // displayError throws ErrorDisplayed
  return ErrorHandler.displayError(errorResponse);
}

/**
 * Normalise an axios-style error (one with a `.response`) to an ErrorResponse
 * and route it through ErrorHandler.displayError (which throws ErrorDisplayed).
 *
 * Always throws — the return type is `never`.
 */
export function displayApiErrorFromAxios(error: unknown): never {
  const response =
    error && typeof error === 'object'
      ? (error as { response?: { status?: number; data?: unknown } }).response
      : undefined;

  const statusCode = response?.status ?? 500;
  const errorResponse = buildErrorResponse(statusCode, response?.data);
  return ErrorHandler.displayError(errorResponse);
}
